import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Loader2, Trash2, RotateCcw, Trash, AlertTriangle, Archive } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { logger } from '../../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TrashBin() {
  const { getAuthHeader } = useAuth();
  const [archived, setArchived] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/quotations/archived`, {
        headers: getAuthHeader(), withCredentials: true,
      });
      setArchived(data);
    } catch (e) {
      logger.error('Failed to load archived:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const restore = async (q) => {
    if (!window.confirm(`Restaurar o card de "${q.name}" para o pipeline?`)) return;
    setBusyId(q.id);
    try {
      await axios.post(`${API}/admin/quotations/${q.id}/restore`, {},
        { headers: getAuthHeader(), withCredentials: true });
      setArchived(prev => prev.filter(x => x.id !== q.id));
    } catch (err) {
      alert('Erro ao restaurar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBusyId(null);
    }
  };

  const purge = async (q) => {
    if (!window.confirm(
      `EXCLUIR PERMANENTEMENTE o card de "${q.name}"?\n\n` +
      `Esta ação NÃO pode ser desfeita. O card será removido do banco de dados para sempre.`
    )) return;
    setBusyId(q.id);
    try {
      await axios.delete(`${API}/admin/quotations/${q.id}/permanent`,
        { headers: getAuthHeader(), withCredentials: true });
      setArchived(prev => prev.filter(x => x.id !== q.id));
    } catch (err) {
      alert('Erro ao excluir: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="animate-spin text-[#22D3EE]" size={40} /></div>;
  }

  return (
    <div className="space-y-6" data-testid="trash-bin-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Archive className="text-[#22D3EE]" size={28} />
            <h1 className="text-3xl font-bold text-[#F8FAFC]">Lixeira</h1>
          </div>
          <p className="text-[#94A3B8]">
            {archived.length} {archived.length === 1 ? 'card arquivado' : 'cards arquivados'} •
            Restaure para o pipeline ou exclua permanentemente
          </p>
        </div>
      </div>

      <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4 flex items-start gap-3">
        <AlertTriangle className="text-amber-400 flex-shrink-0 mt-0.5" size={18} />
        <div className="text-xs text-amber-300/90">
          <strong>Como funciona:</strong> Cards excluídos da Pipeline são movidos para cá automaticamente.
          Eles NÃO contam mais nas métricas. Você pode restaurá-los a qualquer momento
          ou excluí-los definitivamente (sem volta).
        </div>
      </div>

      {archived.length === 0 ? (
        <div className="bg-[#0d0f17] border border-white/5 rounded-xl p-12 text-center">
          <Archive className="text-[#94A3B8]/40 mx-auto mb-4" size={48} />
          <p className="text-[#94A3B8]">A lixeira está vazia.</p>
          <p className="text-xs text-[#94A3B8]/60 mt-2">
            Quando você excluir cards do Pipeline, eles aparecerão aqui.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {archived.map((q) => (
            <motion.div
              key={q.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid={`archived-card-${q.id}`}
              className="bg-[#0d0f17] border border-white/5 hover:border-white/10 rounded-xl p-5 transition-all opacity-90"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-[#F8FAFC] truncate">{q.name}</h3>
                  {q.company && <p className="text-xs text-[#94A3B8] mt-0.5">{q.company}</p>}
                </div>
              </div>
              <div className="space-y-1 mb-3 text-xs text-[#94A3B8]">
                <p>📧 {q.email}</p>
                <p>📞 {q.phone}</p>
              </div>
              <div className="flex items-center gap-1 mb-3 flex-wrap">
                <span className="text-[10px] bg-[#22D3EE]/10 text-[#22D3EE] px-2 py-0.5 rounded">
                  {q.project_type}
                </span>
                <span className="text-[10px] bg-white/5 text-[#94A3B8] px-2 py-0.5 rounded">
                  {q.budget_range}
                </span>
                {q.value > 0 && (
                  <span className="text-[10px] bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded">
                    R$ {Number(q.value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </span>
                )}
              </div>
              {q.archived_at && (
                <p className="text-[10px] text-[#94A3B8]/70 mb-3">
                  Arquivado em {new Date(q.archived_at).toLocaleString('pt-BR')}
                </p>
              )}
              <div className="flex gap-2 pt-3 border-t border-white/5">
                <button
                  onClick={() => restore(q)}
                  disabled={busyId === q.id}
                  data-testid={`restore-${q.id}`}
                  className="flex-1 flex items-center justify-center gap-1.5 p-2 rounded bg-[#22D3EE]/10 hover:bg-[#22D3EE]/20 text-[#22D3EE] text-xs font-semibold disabled:opacity-50 transition-colors"
                >
                  {busyId === q.id ? <Loader2 className="animate-spin" size={14} /> : <RotateCcw size={14} />}
                  Restaurar
                </button>
                <button
                  onClick={() => purge(q)}
                  disabled={busyId === q.id}
                  data-testid={`purge-${q.id}`}
                  className="p-2 rounded bg-red-500/10 hover:bg-red-500/20 text-red-400 disabled:opacity-50 transition-colors"
                  title="Excluir permanentemente"
                >
                  <Trash size={14} />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
